/**
 * Video Studio (UX plan §4.9, phase U6) — scene cards + light timeline over
 * the shipped `AdCreativeVideoPlan.segments[]`. v1 ceiling: 8 scenes / ~60s
 * (decision #9); scenes 5+ chain from the previous clip's last frame.
 *
 * - Scene cards: role label, keyframe thumb, editable motion prompt,
 *   duration select (4/6/8), status pill, «Regenerate this scene only»
 *   (re-renders ONE segment and re-stitches — cost ≈ 1 segment).
 * - Light timeline: proportional track with seams — orientation, not an NLE;
 *   hard cuts; music bed & crossfades are phase 2 (honest note).
 * - Cost estimate always shown before Generate (decision #9).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ReplayIcon from '@mui/icons-material/Replay';
import { StyledTextField } from '@modules/ui-components';
import { ACTIVE_STATUSES, useTaskStore, useTaskTransition } from '@modules/background-tasks';
import type {
  AdCreativeItem,
  AdCreativeVideoPlan,
  AdCreativeVideoSegmentPlan,
} from '@segmently/schema-models/ad-creative';
import type { AiAssetExperiment } from '@segmently/schema-models/content-plan';
import { AD_CREATIVE, AD_CREATIVE_INSIGHT } from '../../selectors';
import type { AdCreativeApiClient } from '../../services/AdCreativeApiClient';
import {
  adCreativeTaskItemId,
  isAdCreativeVideoTask,
} from '../../services/adCreativeTaskScope';
import { useAdCreativeGenSettings } from '../../hooks/useAdCreativeGenSettings';
import {
  INSIGHT_CATEGORY_COLOR,
  SCENE_ROLE_CATEGORIES,
  parseInsightPath,
  type AdCreativeInsightIndex,
  type AdCreativeInsightIndexEntry,
} from '../../services/AdCreativeInsightContextClient';
import type { AdCreativeAngle } from '../batch/batchModel';
import { AD_CREATIVE_VIDEO_STUDIO } from '../../selectors';
import { deriveVideoPhaseRows, deriveVideoUiState } from './videoUiState';

/** Canonical map moved to the selectors registry (FR2); alias kept local. */
const VIDEO_STUDIO = AD_CREATIVE_VIDEO_STUDIO;
export { AD_CREATIVE_VIDEO_STUDIO as VIDEO_STUDIO_SELECTORS };

const VIDEO_MODEL_OPTIONS = [
  { id: 'veo-3.1-fast-generate-preview', label: 'Veo 3.1 Fast (Preview)', estimatedUsdPerSecond: 0.15 },
  { id: 'veo-3.1-generate-preview', label: 'Veo 3.1 (Preview, Best Quality)', estimatedUsdPerSecond: 0.35 },
  { id: 'veo-3.1-lite-generate-preview', label: 'Veo 3.1 Lite (Preview, Cheap Drafts)', estimatedUsdPerSecond: 0.05 },
] as const;

/** Video Studio v1 range: 6–60s (8 scenes × ~8s ceiling, decision #9). */
const DURATION_OPTIONS = [6, 10, 15, 20, 24, 30, 40, 50, 60] as const;
const SCENE_DURATION_OPTIONS = [4, 6, 8] as const;
/** Slow safety net while a video task runs (FR0-PRE-003) — the transition
 * subscription is the primary signal; this only covers missed writes. */
const SAFETY_REFRESH_INTERVAL_MS = 60_000;

type RegenerateMode = 'plan' | 'keyframes' | 'videos' | 'all';

interface VideoStudioProps {
  projectId: string;
  item: AdCreativeItem;
  client: AdCreativeApiClient;
  onRefreshItem: () => Promise<void> | void;
  /** The item's angle (grounded banner + scene hints); optional — legacy callers omit it. */
  angle?: AdCreativeAngle | null;
  /** insightId -> thesis payload of the pinned analysis (scene grounding hints). */
  insightIndex?: AdCreativeInsightIndex;
}

/** First cited thesis whose category grounds the given scene role. */
function sceneGroundingHint(
  role: string,
  angle: AdCreativeAngle | null | undefined,
  insightIndex: AdCreativeInsightIndex | undefined,
): ({ insightId: string } & AdCreativeInsightIndexEntry) | null {
  if (!angle?.insightRefs?.length || !insightIndex) return null;
  const categories = SCENE_ROLE_CATEGORIES[role] ?? [];
  if (categories.length === 0) return null;
  for (const ref of angle.insightRefs) {
    const parsed = parseInsightPath(ref);
    const entry = parsed ? insightIndex[parsed.insightId] : undefined;
    if (entry && categories.includes(entry.category)) return { insightId: parsed!.insightId, ...entry };
  }
  return null;
}

export const VideoStudio = React.memo(function VideoStudio({ projectId, item, client, onRefreshItem, angle, insightIndex }: VideoStudioProps): React.ReactElement {
  const [model, setModel] = useState<string>(VIDEO_MODEL_OPTIONS[0].id);
  const [durationSec, setDurationSec] = useState<number>(10);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [editingScene, setEditingScene] = useState<number | null>(null);
  const [motionDraft, setMotionDraft] = useState('');
  // Latest refresh callback in a ref: neither the transition subscription nor
  // the safety interval re-arms when the parent recreates the prop (F3 fix).
  const onRefreshItemRef = useRef(onRefreshItem);
  onRefreshItemRef.current = onRefreshItem;
  const lastRequestRef = useRef<Parameters<AdCreativeApiClient['generateItemVideo']>[2] | null>(null);
  // FR4: text-agent settings (storyboard model/effort/budget) — the Veo VIDEO
  // model keeps its dedicated select below.
  const genSettings = useAdCreativeGenSettings(projectId);
  const scenesRef = useRef<HTMLDivElement | null>(null);

  const videoPlan = item.videoPlan as AdCreativeVideoPlan | undefined;
  // Explicit FR2 state machine: idle → running → completed | failed.
  const uiState = deriveVideoUiState(item, starting);
  const running = uiState === 'running';
  const videoExperiment = useMemo(() => findVideoExperiment(item, videoPlan?.slotKey), [item, videoPlan?.slotKey]);
  const completed = uiState === 'completed' && Boolean(videoExperiment?.generatedUrl);
  const failed = uiState === 'failed';
  const phaseRows = useMemo(
    () => (running ? deriveVideoPhaseRows(videoPlan) : []),
    [running, videoPlan],
  );

  // The running video task of THIS item (for cancel + live phase visibility).
  const runningTaskId = useTaskStore((state) => {
    for (const task of Object.values(state.tasks)) {
      if (!isAdCreativeVideoTask(task)) continue;
      if (adCreativeTaskItemId(task) !== item.id) continue;
      if (ACTIVE_STATUSES.includes(task.status)) return task.id;
    }
    return null;
  });

  // D4 fix: duration follows the plan target but stays user-editable after
  // completion (regenerate-all with a new target re-plans).
  useEffect(() => {
    if (videoPlan?.targetDurationSec) setDurationSec(videoPlan.targetDurationSec);
  }, [videoPlan?.targetDurationSec]);

  const selectedModel = VIDEO_MODEL_OPTIONS.find((option) => option.id === model) ?? VIDEO_MODEL_OPTIONS[0];
  const planTotalSec = (videoPlan?.segments ?? []).reduce((sum, segment) => sum + segment.durationSec, 0);
  const fullCostEstimate = ((videoPlan ? planTotalSec : durationSec) * selectedModel.estimatedUsdPerSecond).toFixed(2);

  const cancelGeneration = async () => {
    setCancelling(true);
    setError(null);
    try {
      const response = await client.cancelItemVideo(projectId, item.id, {
        taskId: runningTaskId ?? undefined,
      });
      if (!response.success) throw new Error(response.error || 'Failed to cancel video generation');
      // The executor stops at the next checkpoint; the task transition then
      // refreshes this item and unlocks the panel.
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setCancelling(false);
    }
  };

  const scrollToStoryboard = () => {
    scenesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Progress arrives through the app-level background-tasks subscription
  // (FR0-I1) — the old 10s full-item poll is gone. Any status transition of
  // this item's video task refreshes the single item.
  useTaskTransition({
    filter: (task) => isAdCreativeVideoTask(task) && adCreativeTaskItemId(task) === item.id,
    onTransition: () => {
      void onRefreshItemRef.current();
    },
  });

  useEffect(() => {
    if (videoPlan?.status !== 'running') return undefined;
    const interval = window.setInterval(() => {
      void onRefreshItemRef.current();
    }, SAFETY_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [videoPlan?.status]);

  const start = async (body: Parameters<AdCreativeApiClient['generateItemVideo']>[2]) => {
    setStarting(true);
    setError(null);
    setMenuAnchor(null);
    const bodyWithSettings = { ...body, generation: genSettings.requestPayload };
    lastRequestRef.current = bodyWithSettings;
    try {
      const response = await client.generateItemVideo(projectId, item.id, bodyWithSettings);
      if (!response.success) throw new Error(response.error || 'Failed to start video generation');
      await onRefreshItem();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setStarting(false);
    }
  };

  const retryLastRequest = () => start(lastRequestRef.current ?? {
    model: videoPlan?.model ?? model,
    targetDurationSec: videoPlan?.targetDurationSec ?? durationSec,
    regenerate: 'all',
  });

  const regenerateScene = (index: number, patches?: Array<{ index: number; motionPrompt?: string; durationSec?: number }>) =>
    start({
      model: videoPlan?.model ?? model,
      targetDurationSec: videoPlan?.targetDurationSec ?? durationSec,
      regenerate: 'segment',
      segmentIndex: index,
      segmentPatches: patches,
    });

  return (
    <Box data-testid={VIDEO_STUDIO.root} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Video Studio</Typography>
          <Chip size="small" variant="outlined" label={videoPlan?.aspectRatio ?? '9:16'} />
          {videoPlan?.styleLock && (
            <Chip size="small" variant="outlined" label="Style locked across scenes" title={videoPlan.styleLock} />
          )}
        </Stack>

        {/* Grounded banner (mock 04): what the storyboard agent receives. */}
        {angle?.insightRefs?.length ? (
          <Box
            data-testid={AD_CREATIVE_INSIGHT.videoGroundedBanner}
            sx={{ pl: 1.25, py: 0.75, borderLeft: 3, borderColor: 'info.main', bgcolor: 'action.hover', borderRadius: '0 8px 8px 0' }}
          >
            <Typography variant="caption">
              <b>Storyboard grounded in:</b>
              {` angle “${angle.label}” · ${angle.insightRefs.length} cited thes${angle.insightRefs.length === 1 ? 'is' : 'es'} → scene roles`}
            </Typography>
          </Box>
        ) : null}

        {/* Wrap-friendly controls: MUI breakpoints track the VIEWPORT, not the
            420px drawer — fixed row mins used to overflow and clip the panel. */}
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <StyledTextField
            select
            label="Video model"
            value={model}
            data-testid={VIDEO_STUDIO.model}
            onChange={(value) => setModel(String(value))}
            disabled={running}
            size="small"
            sx={{ flex: '1 1 200px', minWidth: 170 }}
          >
            {VIDEO_MODEL_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
            ))}
          </StyledTextField>
          <StyledTextField
            select
            label="Target duration"
            value={String(durationSec)}
            data-testid={VIDEO_STUDIO.duration}
            onChange={(value) => setDurationSec(Number(value))}
            disabled={running}
            size="small"
            sx={{ flex: '0 1 130px', minWidth: 110 }}
          >
            {DURATION_OPTIONS.map((option) => (
              <MenuItem key={option} value={String(option)}>{option} s</MenuItem>
            ))}
          </StyledTextField>
          {uiState === 'idle' && (
            <Button
              data-testid={VIDEO_STUDIO.generate}
              variant="contained"
              size="small"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => void start({ model, targetDurationSec: durationSec })}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Generate video
            </Button>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary" data-testid={VIDEO_STUDIO.cost}>
          {`≈ $${fullCostEstimate} full render · estimated — per-scene regenerate costs ≈ 1 scene`}
        </Typography>
        {uiState === 'idle' && (
          <Typography variant="caption" color="text.secondary" data-testid={VIDEO_STUDIO.idleCaption}>
            Storyboard is generated first — you review scenes before paid rendering.
          </Typography>
        )}

        {/* RUNNING (mock 06 ②): phase checklist + Cancel + locked-settings caption. */}
        {running && (
          <Stack spacing={1} data-testid={VIDEO_STUDIO.phaseChecklist}>
            {phaseRows.map((row) => (
              <Stack key={row.key} direction="row" spacing={1} alignItems="center" data-testid={VIDEO_STUDIO.phaseRow(row.key)}>
                {row.status === 'running'
                  ? <CircularProgress size={12} />
                  : (
                    <Typography variant="caption" color={row.status === 'done' ? 'success.main' : 'text.disabled'}>
                      {row.status === 'done' ? '✓' : '·'}
                    </Typography>
                  )}
                <Typography
                  variant="caption"
                  color={row.status === 'todo' ? 'text.disabled' : 'text.primary'}
                >
                  {row.label}
                </Typography>
              </Stack>
            ))}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                color="error"
                data-testid={VIDEO_STUDIO.cancel}
                disabled={cancelling}
                startIcon={cancelling ? <CircularProgress size={12} /> : undefined}
                onClick={() => void cancelGeneration()}
              >
                {cancelling ? 'Cancelling…' : 'Cancel generation'}
              </Button>
              <Button size="small" data-testid={VIDEO_STUDIO.storyboardLink} onClick={scrollToStoryboard}>
                Open storyboard ↗
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" data-testid={VIDEO_STUDIO.lockCaption}>
              🔒 Settings locked while running — cancel to change. Completed scenes are kept; cancel stops at the next scene boundary.
            </Typography>
          </Stack>
        )}

        {error && <Alert severity="error">{error}</Alert>}
        {failed && (
          <Alert
            severity="error"
            data-testid={AD_CREATIVE.videoError}
            action={(
              <Button
                size="small"
                color="inherit"
                data-testid={VIDEO_STUDIO.retry}
                onClick={() => void retryLastRequest()}
              >
                Retry
              </Button>
            )}
          >
            {videoPlan?.error || 'Video generation failed.'} Completed scenes keep their clips.
          </Alert>
        )}

        {videoPlan && videoPlan.segments.length > 0 && (
          <>
            <Box
              ref={scenesRef}
              sx={{
                display: 'grid',
                // Single minmax(0,1fr) column: the studio lives in the 420px
                // drawer, so viewport breakpoints lie here, and a plain `1fr`
                // lets a wide min-content prompt blow the track past the
                // drawer edge (FR6 journey finding F1 — card unreachable).
                gridTemplateColumns: 'minmax(0, 1fr)',
                gap: 1,
              }}
            >
              {videoPlan.segments.map((segment) => (
                <SceneCard
                  key={segment.index}
                  item={item}
                  segment={segment}
                  running={running}
                  grounded={Boolean(angle?.insightRefs?.length)}
                  groundingHint={sceneGroundingHint(
                    String((segment as unknown as Record<string, unknown>).role ?? ''),
                    angle,
                    insightIndex,
                  )}
                  editing={editingScene === segment.index}
                  motionDraft={motionDraft}
                  onStartEdit={() => {
                    setEditingScene(segment.index);
                    setMotionDraft(segment.motionPrompt);
                  }}
                  onCancelEdit={() => setEditingScene(null)}
                  onMotionDraftChange={setMotionDraft}
                  onApplyEdit={() => {
                    setEditingScene(null);
                    void regenerateScene(segment.index, [{ index: segment.index, motionPrompt: motionDraft }]);
                  }}
                  onDurationChange={(nextDuration) => {
                    void regenerateScene(segment.index, [{ index: segment.index, durationSec: nextDuration }]);
                  }}
                  onRegenerate={() => void regenerateScene(segment.index)}
                />
              ))}
            </Box>

            <Box data-testid={VIDEO_STUDIO.timeline}>
              <Stack direction="row" spacing={0.25} sx={{ height: 18, borderRadius: 0.75, overflow: 'hidden' }}>
                {videoPlan.segments.map((segment) => (
                  <Box
                    key={segment.index}
                    sx={{
                      flex: segment.durationSec,
                      bgcolor: segmentColor(segment.status),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={`${segment.index + 1} · ${segment.durationSec}s · ${segment.status}`}
                  >
                    <Typography variant="caption" sx={{ color: 'common.white', fontSize: 10 }}>
                      {`${segment.durationSec}s`}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.25 }}>
                <Typography variant="caption" color="text.secondary">
                  hard cuts · music bed & crossfades — phase 2
                </Typography>
                <Typography variant="caption" color="text.secondary" data-testid={VIDEO_STUDIO.timelineTotal}>
                  {`total ${planTotalSec}s / target ${videoPlan.targetDurationSec}s`}
                </Typography>
              </Stack>
            </Box>
          </>
        )}

        {completed && videoExperiment && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Box
              component="video"
              controls
              data-testid={VIDEO_STUDIO.player}
              poster={videoExperiment.posterUrl}
              src={videoExperiment.generatedUrl}
              sx={{ width: 236, maxWidth: '100%', borderRadius: 1, bgcolor: 'grey.100' }}
            />
            <Stack spacing={1} sx={{ flex: 1, minWidth: 200 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  variant="outlined"
                  data-testid={AD_CREATIVE.videoDurationBadge}
                  label={`${(videoExperiment.durationSec ?? videoPlan?.totalDurationSec ?? 0).toFixed(1)} s`}
                />
                <Chip size="small" variant="outlined" label={`${videoPlan?.resolution ?? '720p'} · ${videoPlan?.aspectRatio ?? '9:16'}`} />
                <Chip
                  size="small"
                  variant="outlined"
                  color={durationWithinBounds(videoPlan, videoExperiment) ? 'success' : 'error'}
                  data-testid={AD_CREATIVE.videoValidationChip('duration_bounds')}
                  label={`${durationWithinBounds(videoPlan, videoExperiment) ? '✓' : '✕'} duration_bounds`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color={videoExperiment.posterUrl ? 'success' : 'error'}
                  data-testid={AD_CREATIVE.videoValidationChip('thumbnail_selected')}
                  label={`${videoExperiment.posterUrl ? '✓' : '✕'} thumbnail_selected`}
                />
              </Stack>
            </Stack>
          </Stack>
        )}

        {(completed || failed) && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              data-testid={VIDEO_STUDIO.regenerateMenu}
              variant="outlined"
              size="small"
              endIcon={<ArrowDropDownIcon />}
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              disabled={running}
            >
              Regenerate
            </Button>
            <Button size="small" data-testid={VIDEO_STUDIO.storyboardLink} onClick={scrollToStoryboard}>
              Open storyboard ↗
            </Button>
            <Typography variant="caption" color="text.secondary">
              per-scene edits & re-renders live in the scene cards
            </Typography>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              {(['plan', 'keyframes', 'videos', 'all'] as RegenerateMode[]).map((mode) => (
                <MenuItem
                  key={mode}
                  data-testid={AD_CREATIVE.videoRegenerateOption(mode)}
                  onClick={() => void start({
                    model: videoPlan?.model ?? model,
                    // Full re-plans honour the CURRENT duration selection (D4
                    // fix); partial regens keep the plan's existing target.
                    targetDurationSec: mode === 'plan' || mode === 'all'
                      ? durationSec
                      : videoPlan?.targetDurationSec ?? durationSec,
                    regenerate: mode,
                  })}
                >
                  {mode === 'plan' ? 'Replan storyboard'
                    : mode === 'keyframes' ? 'Regenerate keyframes'
                      : mode === 'videos' ? 'Regenerate videos (reuse keyframes)'
                        : 'Regenerate all'}
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        )}
      </Stack>
    </Box>
  );
});

function SceneCard({
  item,
  segment,
  running,
  editing,
  motionDraft,
  onStartEdit,
  onCancelEdit,
  onMotionDraftChange,
  onApplyEdit,
  onDurationChange,
  onRegenerate,
  groundingHint,
  grounded,
}: {
  item: AdCreativeItem;
  segment: AdCreativeVideoSegmentPlan;
  running: boolean;
  editing: boolean;
  motionDraft: string;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onMotionDraftChange: (value: string) => void;
  onApplyEdit: () => void;
  onDurationChange: (durationSec: number) => void;
  onRegenerate: () => void;
  groundingHint?: ({ insightId: string } & AdCreativeInsightIndexEntry) | null;
  grounded?: boolean;
}): React.ReactElement {
  const role = (segment as unknown as Record<string, unknown>).role;
  const label = `${segment.index + 1}·${String(role ?? 'scene').toUpperCase()}`;
  const keyframeUrl = findExperimentUrl(item, segment.keyframeSlotKey);
  const isChain = segment.continuityMode === 'chain';
  const roleKey = String(role ?? '');

  return (
    <Stack
      direction="row"
      spacing={1}
      data-testid={VIDEO_STUDIO.sceneCard(segment.index)}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
    >
      {keyframeUrl ? (
        <Box
          component="img"
          src={keyframeUrl}
          alt={label}
          loading="lazy"
          decoding="async"
          sx={{ width: 44, height: 78, objectFit: 'cover', borderRadius: 0.75, border: 1, borderColor: 'divider', flexShrink: 0 }}
        />
      ) : (
        <Box sx={{ width: 44, height: 78, borderRadius: 0.75, border: 1, borderColor: 'divider', bgcolor: 'grey.100', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9, textAlign: 'center' }}>
            {isChain ? 'chain' : 'kf'}
          </Typography>
        </Box>
      )}
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }} noWrap>{label}</Typography>
          {isChain && <Chip size="small" variant="outlined" label="chain" sx={{ height: 18 }} />}
          <Chip
            size="small"
            color={segmentChipColor(segment.status)}
            label={segment.status.replace(/_/g, ' ')}
            data-testid={AD_CREATIVE.videoSegmentChip(segment.index)}
            sx={{ height: 18 }}
          />
        </Stack>
        {editing ? (
          <Stack spacing={0.5}>
            <StyledTextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={motionDraft}
              data-testid={VIDEO_STUDIO.sceneMotion(segment.index)}
              onChange={(value) => onMotionDraftChange(String(value))}
            />
            <Stack direction="row" spacing={0.5}>
              <Button
                size="small"
                variant="contained"
                data-testid={VIDEO_STUDIO.sceneApply(segment.index)}
                disabled={running || !motionDraft.trim()}
                onClick={onApplyEdit}
              >
                Apply & re-render scene
              </Button>
              <Button size="small" onClick={onCancelEdit}>Cancel</Button>
            </Stack>
          </Stack>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            onClick={running ? undefined : onStartEdit}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              cursor: running ? 'default' : 'text',
            }}
            title="Click to edit the motion prompt"
          >
            {segment.motionPrompt}
          </Typography>
        )}
        {/* Grounding hint (mock 04): which thesis this scene realizes. */}
        {grounded && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="flex-start"
            data-testid={AD_CREATIVE_INSIGHT.videoSceneHint(segment.index)}
            sx={{ borderTop: 1, borderColor: 'divider', borderTopStyle: 'dashed', pt: 0.5 }}
          >
            {groundingHint ? (
              <>
                <Chip
                  size="small"
                  color={INSIGHT_CATEGORY_COLOR[groundingHint.category] ?? 'default'}
                  label={groundingHint.category}
                  sx={{ height: 16, fontSize: 9, flexShrink: 0 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap title={groundingHint.thesis}>
                  {groundingHint.thesis}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {roleKey === 'cta' ? 'no source needed — brand close' : 'no mapped source'}
              </Typography>
            )}
          </Stack>
        )}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <StyledTextField
            select
            size="small"
            value={String(segment.durationSec)}
            data-testid={VIDEO_STUDIO.sceneDuration(segment.index)}
            onChange={(value) => onDurationChange(Number(value))}
            disabled={running}
            sx={{ width: 72 }}
          >
            {SCENE_DURATION_OPTIONS.map((option) => (
              <MenuItem key={option} value={String(option)}>{option}s</MenuItem>
            ))}
          </StyledTextField>
          <Button
            size="small"
            startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
            data-testid={VIDEO_STUDIO.sceneRegenerate(segment.index)}
            disabled={running}
            onClick={onRegenerate}
          >
            This scene only
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

function segmentColor(status: AdCreativeVideoSegmentPlan['status']): string {
  if (status === 'completed') return 'success.main';
  if (status === 'generating') return 'info.main';
  if (status === 'error') return 'error.main';
  return 'grey.400';
}

function segmentChipColor(status: AdCreativeVideoSegmentPlan['status']): 'default' | 'success' | 'info' | 'error' {
  if (status === 'completed') return 'success';
  if (status === 'generating') return 'info';
  if (status === 'error') return 'error';
  return 'default';
}

function durationWithinBounds(videoPlan?: AdCreativeVideoPlan, experiment?: AiAssetExperiment): boolean {
  const target = Number(videoPlan?.targetDurationSec);
  const measured = Number(experiment?.durationSec ?? videoPlan?.totalDurationSec);
  if (!Number.isFinite(target) || !Number.isFinite(measured) || target <= 0 || measured <= 0) return false;
  return Math.abs(measured - target) / target <= 0.2;
}

function findVideoExperiment(item: AdCreativeItem, slotKey?: string): AiAssetExperiment | undefined {
  const aiAssets = item.aiAssets as Record<string, { selectedExperimentId?: string; experiments?: Record<string, AiAssetExperiment> }> | undefined;
  if (!aiAssets) return undefined;
  const slots = slotKey && aiAssets[slotKey] ? [aiAssets[slotKey]] : Object.values(aiAssets);
  for (const slot of slots) {
    const selected = slot.selectedExperimentId ? slot.experiments?.[slot.selectedExperimentId] : undefined;
    if (selected?.assetType === 'video') return selected;
    const anyVideo = Object.values(slot.experiments ?? {}).find((experiment) => experiment.assetType === 'video');
    if (anyVideo) return anyVideo;
  }
  return undefined;
}

function findExperimentUrl(item: AdCreativeItem, slotKey: string): string | undefined {
  const aiAssets = item.aiAssets as Record<string, { selectedExperimentId?: string; experiments?: Record<string, AiAssetExperiment> }> | undefined;
  const slot = aiAssets?.[slotKey];
  if (!slot?.experiments) return undefined;
  const selected = slot.selectedExperimentId ? slot.experiments[slot.selectedExperimentId] : Object.values(slot.experiments)[0];
  return selected?.status === 'completed' ? selected.generatedUrl : undefined;
}
